import React, { createContext, useContext, useMemo } from 'react'

export interface ViewerExpandContextValue {
  /** Whether the host has given the viewer everything it has. */
  expanded: boolean
  /** Swap between the two. */
  onToggle: () => void
}

const ViewerExpandContext = createContext<ViewerExpandContextValue | null>(null)

interface ViewerExpandProviderProps extends ViewerExpandContextValue {
  children: React.ReactNode
}

/**
 * Offer whatever is showing a file the run of the surface it is shown in.
 *
 * The two halves are far apart in the tree: a pane in the files explorer knows
 * it can grow to fill the page, and the control that would ask it to belongs
 * down in the viewer's own toolbar, under three components that pass files
 * around and have no business carrying a flag about panes. So the host says it
 * can here, and the toolbar asks here.
 */
export const ViewerExpandProvider: React.FC<ViewerExpandProviderProps> = ({
  expanded,
  onToggle,
  children,
}) => {
  const value = useMemo(() => ({ expanded, onToggle }), [expanded, onToggle])
  return (
    <ViewerExpandContext.Provider value={value}>
      {children}
    </ViewerExpandContext.Provider>
  )
}

/** Null where the host has nowhere bigger to put the file. */
export const useViewerExpand = (): ViewerExpandContextValue | null =>
  useContext(ViewerExpandContext)
